import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { debounce, isString } from 'lodash'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { DEFAULT_INTERNSHIP_FILTERS, GlobalFiltersContext } from '../../../Providers/GlobalFiltersProvider'
import { getFirstMondayOfMonth } from '../../../utils/momentjs'
import { Contexts, DATE_WITHOUT_TIME, InternshipStates, InternshipStatesString } from '../../../utils/constants'
import { acceptMaccsInternship, cancelMaccsInternship, deleteMaccsInternship, duplicateMaccsInternship, getAllMaccsInternships, resetToPending } from '../../../utils/api/maccsInternship'
import { onError, onSuccess, purgeMetadata } from '../../../utils/apiHelper'
import Header from './Header'
import CalendarTable from './CalendarTable'
import MaccsInternshipModal from '../../../Providers/Forms/MaccsInternshipModal/MaccsInternshipModal'
import { getJob } from '../../../utils/api/asynchronous'
import { isObject } from '../../../utils'
import { MaccsInternshipsContext } from '../../../Providers/MaccsInternshipsProvider'
import { MaccsInternshipMessageContext } from '../../../Providers/MaccsinternshipMessageProvider'

import '../../../assets/maccs-internships-calendar.scss'

const DEFAULT_META_DATA = {
  page: 1,
  last: 0,
  pageSize: 10,
  totalPages: 1,
  total: 0
}
const TIME_BEFORE_SEARCH = 200

const MaccsInternshipsCalendar = ({ loadShiftsManager }) => {
  const { setTrackedInternships } = useContext(MaccsInternshipMessageContext)
  const { setInternshipToDelete } = useContext(MaccsInternshipsContext)
  const { internshipDate, internshipFilters, internshipSearch, setInternshipFilters, setInternshipSearch } = useContext(GlobalFiltersContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [internships, setInternships] = useState([])
  const [metadata, setMetadata] = useState(DEFAULT_META_DATA)
  const [loading, setLoading] = useState(false)

  const period = useMemo(() => ({
    startDate: getFirstMondayOfMonth(internshipDate),
    endDate: internshipDate.clone().add(47, 'week').endOf('isoWeek').format(DATE_WITHOUT_TIME)
  }), [internshipDate.format(DATE_WITHOUT_TIME), internshipFilters.weeksShownCount])

  const parameters = useMemo(() => ({
    order: internshipFilters.order,
    page: metadata.page,
    pageSize: metadata.pageSize,
    startDate: period.startDate,
    endDate: period.endDate,
    search: internshipSearch,
    filters: {
      schools: isString(internshipFilters.schools) ? [internshipFilters.schools] : internshipFilters.schools,
      institutions: isString(internshipFilters.institutions) ? [internshipFilters.institutions] : internshipFilters.institutions,
      specialities: internshipFilters.specialities,
      states: internshipFilters.states,
      workingRegime: internshipFilters.workingRegime,
      studyYears: internshipFilters.studyYears
    }
  }), [internshipFilters, internshipSearch, metadata.page, period])

  const fetchInternships = useCallback(() => {
    setLoading(true)

    const urlParameters = { ...parameters }

    if (user.context === Contexts.SCHOOL) {
      urlParameters.contexts = [Contexts.SCHOOL.toLowerCase()]
    }

    getAllMaccsInternships(user, urlParameters).then(({ data, meta }) => {
      setInternships(data)
      setTrackedInternships(data.map(i => i.id))

      setMetadata(purgeMetadata(meta.pages))

      setLoading(false)
    })
  }, [user, parameters])

  useEffect(() => {
    const handler = debounce(() => fetchInternships(), TIME_BEFORE_SEARCH)

    handler()

    return () => {
      handler.cancel()
    }
  }, [fetchInternships])

  const handleResetFilters = () => {
    setInternshipSearch('')
    setInternshipFilters(DEFAULT_INTERNSHIP_FILTERS)
  }

  const handleDelete = internship => {
    deleteMaccsInternship(user, internship).then(() => {
      onSuccess(t('maccs_internships_calendar.delete_internship.success'))
      fetchInternships()
    }).catch(() => {
      onError(t('maccs_internships_calendar.delete_internship.error'))
    })
  }

  const handleDuplicate = internship => {
    duplicateMaccsInternship(user, internship).then(({ job }) => {
      let incr = 0
      const interval = setInterval(() => getJob(job, user).then(({ data }) => {
        clearInterval(interval)
        fetchInternships()
        onSuccess(t('maccs_internships_calendar.duplicate_internship.success'))
      }).catch(() => {
        incr++

        if (incr >= 20) {
          clearInterval(interval)
          onError(t('maccs_internships_calendar.duplicate_internship.error'))
        }
      }), 1000)
    }).catch(() => {
      onError(t('maccs_internships_calendar.duplicate_internship.error'))
    })
  }

  const handleStateChange = (internship, state) => {
    const { REFUSED, PENDING, ACCEPTED, CANCELED } = InternshipStates
    const actions = {
      [PENDING]: { promise: resetToPending, action: InternshipStatesString[PENDING] },
      [ACCEPTED]: { promise: acceptMaccsInternship, action: InternshipStatesString[ACCEPTED] },
      [CANCELED]: { promise: cancelMaccsInternship, action: InternshipStatesString[CANCELED] }
    }
    const action = actions[state]

    if (isObject(action)) {
      action.promise(user, internship).then(() => {
        onSuccess(t(`maccs_internships_calendar.state_change.${action.action.toLowerCase()}.success`))
        fetchInternships()
      }).catch(() => {
        onError(t(`maccs_internships_calendar.state_change.${action.action.toLowerCase()}.error`))
      })
    } else if (state === REFUSED) {
      setInternshipToDelete(internship, fetchInternships)
    }
  }

  return (
    <MaccsInternshipModal>
      <Header
        metadata={metadata}
        loading={loading}
        onPageChange={newPage => setMetadata(prev => ({ ...prev, page: newPage }))}
        onResetFilters={handleResetFilters}
        refresh={fetchInternships}
      />
      <CalendarTable
        internships={internships}
        loading={loading}
        currentDate={internshipDate}
        refresh={fetchInternships}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onStateChange={handleStateChange}
      />
    </MaccsInternshipModal>
  )
}

export default MaccsInternshipsCalendar
